import {
  EXPORT_FORMAT_LABELS,
  type ExportFormat,
  formatBytes,
  TRANSCRIPT_LANGUAGES,
  type TranscriptLanguage,
} from "@cva/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Captions,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CircleAlert,
  Clock,
  Download,
  FileOutput,
  Film,
  ListChecks,
  Loader2,
  PersonStanding,
  RefreshCw,
  Upload,
  Wand2,
  X,
} from "lucide-react";
import { useEffect, useSyncExternalStore } from "react";
import { toast } from "sonner";
import {
  type ActivityGroup,
  dismissGroups,
  estimateProgress,
  formatClock,
  formatRemaining,
  isActive,
  type Task,
  uploadRate,
  useActivity,
  useElapsed,
  useNow,
} from "../lib/activity";
import { orpc } from "../lib/api";
import { cancelUpload, dismissUpload } from "../lib/uploads";
import { cn, errorMessage } from "../lib/utils";
import { Progress } from "./ui/card";

// ---------------------------------------------------------------------------
// One step (upload, preparation, speech recognition, export)
// ---------------------------------------------------------------------------

interface StepView {
  icon: React.ReactNode;
  label: string;
  /** null: no bar; "indeterminate": runs without known progress. */
  progress: number | null | "indeterminate";
  detail: string | null;
  error: string | null;
  tone: "normal" | "done" | "failed";
  action?: React.ReactNode;
}

const MEDIA_STEPS: Record<string, string> = {
  upload: "Datei wird verarbeitet",
  audio: "Tonspur wird gelesen",
  proxy: "Vorschau wird erstellt",
  filmstrip: "Filmstreifen wird erstellt",
};

const shortModel = (label: string | null) => label?.replace(/\s*\(.*\)$/, "") ?? null;

const join = (...parts: (string | null | false | undefined)[]) =>
  parts.filter(Boolean).join(" · ") || null;

function UploadStep({ task }: { task: Extract<Task, { kind: "upload" }> }) {
  const item = task.upload;
  const now = useNow(item.status === "uploading");
  const rate = item.status === "uploading" ? uploadRate(item, now) : null;
  const view: StepView =
    item.status === "uploading"
      ? {
          icon: <Upload className="text-brand-600" />,
          label: "Wird hochgeladen",
          progress: (item.uploaded / Math.max(item.size, 1)) * 100,
          detail: join(
            `${formatBytes(item.uploaded)} von ${formatBytes(item.size)}`,
            rate && `${formatBytes(rate.rate)}/s`,
            rate && formatRemaining(rate.remainingMs),
          ),
          error: null,
          tone: "normal",
          action: (
            <button
              type="button"
              onClick={() => cancelUpload(item.id)}
              className="text-xs text-zinc-500 hover:text-zinc-900"
            >
              Abbrechen
            </button>
          ),
        }
      : item.status === "done"
        ? {
            icon: <CheckCircle2 className="text-emerald-600" />,
            label: "Hochgeladen",
            progress: null,
            detail: formatBytes(item.size),
            error: null,
            tone: "done",
          }
        : item.status === "error"
          ? {
              icon: <CircleAlert className="text-red-600" />,
              label: "Upload fehlgeschlagen",
              progress: null,
              detail: null,
              error: item.error ?? "Upload fehlgeschlagen",
              tone: "failed",
            }
          : {
              icon: <X className="text-zinc-400" />,
              label: "Upload abgebrochen",
              progress: null,
              detail: null,
              error: null,
              tone: "done",
            };
  return <StepRow view={view} />;
}

function RetryTranscription({
  orgId,
  assetId,
  language,
}: {
  orgId: string;
  assetId: string;
  language: string | null;
}) {
  const queryClient = useQueryClient();
  const retry = useMutation(
    orpc.media.transcribe.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: orpc.activity.key() });
        void queryClient.invalidateQueries({ queryKey: orpc.media.transcript.key() });
      },
      onError: (error) => toast.error(errorMessage(error)),
    }),
  );
  const lang = (TRANSCRIPT_LANGUAGES as readonly string[]).includes(language ?? "")
    ? (language as TranscriptLanguage)
    : "auto";
  return (
    <button
      type="button"
      disabled={retry.isPending}
      onClick={() => retry.mutate({ orgId, assetId, language: lang })}
      className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700 disabled:opacity-50"
    >
      <RefreshCw className="size-3" /> Erneut versuchen
    </button>
  );
}

function ServerStep({
  task,
  orgId,
}: {
  task: Extract<Task, { kind: "media" | "transcript" | "export" | "cutout" | "matte" }>;
  orgId: string;
}) {
  const item = task.activity;
  const elapsed = useElapsed(item.status === "running" ? item.elapsedMs : null);
  const since = elapsed !== null && elapsed > 3000 ? `läuft seit ${formatClock(elapsed)}` : null;
  const waiting =
    item.queuePosition && item.queuePosition > 0
      ? `${item.queuePosition} ${item.queuePosition === 1 ? "Auftrag" : "Aufträge"} vor dir`
      : "startet gleich";
  let view: StepView;
  if (item.kind === "media") {
    view =
      item.status === "failed"
        ? {
            icon: <CircleAlert className="text-red-600" />,
            label: "Vorbereitung fehlgeschlagen",
            progress: null,
            detail: null,
            error: item.error,
            tone: "failed",
          }
        : {
            icon: <Film className="text-brand-600" />,
            label: MEDIA_STEPS[item.step ?? ""] ?? "Video wird vorbereitet",
            progress: item.progress ? item.progress : "indeterminate",
            detail: since,
            error: null,
            tone: "normal",
          };
  } else if (item.kind === "transcript") {
    const estimate = estimateProgress(item.progress ?? 0, elapsed, item.estimatedMs);
    const remaining =
      estimate.remainingMs === null
        ? null
        : estimate.remainingMs < -15_000
          ? "dauert länger als erwartet"
          : formatRemaining(Math.max(0, estimate.remainingMs));
    view =
      item.status === "queued"
        ? {
            icon: <Clock className="text-zinc-500" />,
            label: "Untertitel: in der Warteschlange",
            progress: null,
            detail: waiting,
            error: null,
            tone: "normal",
          }
        : item.status === "running"
          ? {
              icon: <Captions className="text-brand-600" />,
              label: "Untertitel werden erkannt",
              progress: estimate.progress,
              detail: join(since, remaining, shortModel(item.detail)),
              error: null,
              tone: "normal",
            }
          : item.status === "done"
            ? {
                icon: <CheckCircle2 className="text-emerald-600" />,
                label: "Untertitel erkannt",
                progress: null,
                detail: shortModel(item.detail),
                error: null,
                tone: "done",
              }
            : {
                icon: <CircleAlert className="text-red-600" />,
                label: "Spracherkennung fehlgeschlagen",
                progress: null,
                detail: null,
                error: item.error,
                tone: "failed",
                action: item.assetId ? (
                  <RetryTranscription
                    orgId={orgId}
                    assetId={item.assetId}
                    language={item.language}
                  />
                ) : undefined,
              };
  } else if (item.kind === "matte") {
    const estimate = estimateProgress(item.progress ?? 0, elapsed, item.estimatedMs);
    view =
      item.status === "queued"
        ? {
            icon: <Clock className="text-zinc-500" />,
            label: "Person freistellen: in der Warteschlange",
            progress: null,
            detail: waiting,
            error: null,
            tone: "normal",
          }
        : item.status === "running"
          ? {
              icon: <PersonStanding className="text-brand-600" />,
              label: "Person wird freigestellt",
              progress: estimate.progress,
              detail: join(
                since,
                estimate.remainingMs === null
                  ? null
                  : formatRemaining(Math.max(0, estimate.remainingMs)),
              ),
              error: null,
              tone: "normal",
            }
          : item.status === "done"
            ? {
                icon: <CheckCircle2 className="text-emerald-600" />,
                label: "Person freigestellt",
                progress: null,
                detail: null,
                error: null,
                tone: "done",
              }
            : {
                icon: <CircleAlert className="text-red-600" />,
                label: "Freistellen fehlgeschlagen",
                progress: null,
                detail: null,
                error: item.error,
                tone: "failed",
              };
  } else if (item.kind === "cutout") {
    const estimate = estimateProgress(item.progress ?? 0, elapsed, item.estimatedMs);
    view =
      item.status === "queued"
        ? {
            icon: <Clock className="text-zinc-500" />,
            label: "Freistellen: in der Warteschlange",
            progress: null,
            detail: waiting,
            error: null,
            tone: "normal",
          }
        : item.status === "running"
          ? {
              icon: <Wand2 className="text-brand-600" />,
              label: "Hintergrund wird entfernt",
              progress: estimate.progress,
              detail: join(
                since,
                estimate.remainingMs === null
                  ? null
                  : formatRemaining(Math.max(0, estimate.remainingMs)),
              ),
              error: null,
              tone: "normal",
            }
          : item.status === "done"
            ? {
                icon: <CheckCircle2 className="text-emerald-600" />,
                label: "Freigestellt – liegt in der Mediathek",
                progress: null,
                detail: null,
                error: null,
                tone: "done",
              }
            : {
                icon: <CircleAlert className="text-red-600" />,
                label: "Freistellen fehlgeschlagen",
                progress: null,
                detail: null,
                error: item.error,
                tone: "failed",
              };
  } else {
    const format = EXPORT_FORMAT_LABELS[item.step as ExportFormat] ?? item.step ?? "Export";
    view =
      item.status === "queued"
        ? {
            icon: <Clock className="text-zinc-500" />,
            label: `${format}: in der Warteschlange`,
            progress: null,
            detail: waiting,
            error: null,
            tone: "normal",
          }
        : item.status === "running"
          ? {
              icon: <FileOutput className="text-brand-600" />,
              label: `${format} wird exportiert`,
              progress: item.progress || "indeterminate",
              detail: since,
              error: null,
              tone: "normal",
            }
          : item.status === "done"
            ? {
                icon: <CheckCircle2 className="text-emerald-600" />,
                label: `${format} fertig`,
                progress: null,
                detail: null,
                error: null,
                tone: "done",
                action: item.url ? (
                  <a
                    href={item.url}
                    download
                    className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700"
                  >
                    <Download className="size-3" /> Herunterladen
                  </a>
                ) : undefined,
              }
            : {
                icon: <CircleAlert className="text-red-600" />,
                label: `${format}: Export fehlgeschlagen`,
                progress: null,
                detail: null,
                error: item.error,
                tone: "failed",
              };
  }
  return <StepRow view={view} />;
}

function StepRow({ view }: { view: StepView }) {
  return (
    <div className="py-1">
      <div className="flex items-center gap-2 text-xs">
        <span className="flex size-4 shrink-0 items-center justify-center [&_svg]:size-3.5">
          {view.icon}
        </span>
        <span
          className={cn(
            "min-w-0 flex-1 truncate",
            view.tone === "done" ? "text-zinc-500" : "text-zinc-800",
            view.tone === "failed" && "text-red-700",
          )}
        >
          {view.label}
          {typeof view.progress === "number" ? (
            <span className="text-zinc-500"> · {Math.floor(view.progress)} %</span>
          ) : null}
        </span>
        {view.action}
      </div>
      {view.progress !== null ? (
        <div className="mt-1 ml-6">
          {view.progress === "indeterminate" ? (
            <div className="relative h-1.5 overflow-hidden rounded-full bg-zinc-100">
              <div className="absolute inset-y-0 w-1/3 animate-activity rounded-full bg-brand-500" />
            </div>
          ) : (
            <Progress className="h-1.5" value={view.progress} />
          )}
        </div>
      ) : null}
      {view.detail ? <p className="mt-0.5 ml-6 text-[11px] text-zinc-500">{view.detail}</p> : null}
      {view.error ? <p className="mt-0.5 ml-6 text-[11px] text-red-600">{view.error}</p> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Groups and lists
// ---------------------------------------------------------------------------

function dismiss(groups: ActivityGroup[]) {
  for (const group of groups) {
    for (const task of group.tasks) if (task.kind === "upload") dismissUpload(task.upload.id);
  }
  dismissGroups(groups);
}

function GroupIcon({ group }: { group: ActivityGroup }) {
  if (isActive(group.status)) return <Loader2 className="size-4 animate-spin text-brand-600" />;
  if (group.status === "failed") return <CircleAlert className="size-4 text-red-600" />;
  return <CheckCircle2 className="size-4 text-emerald-600" />;
}

export function ActivityList({ groups, orgId }: { groups: ActivityGroup[]; orgId: string }) {
  if (groups.length === 0) {
    return <p className="px-4 py-6 text-center text-sm text-zinc-500">Gerade läuft nichts.</p>;
  }
  return (
    <ul className="divide-y divide-zinc-100">
      {groups.map((group) => (
        <li key={group.key} className="px-4 py-2.5">
          <div className="flex items-center gap-2">
            <GroupIcon group={group} />
            <span className="min-w-0 flex-1 truncate text-sm font-medium" title={group.title}>
              {group.title}
            </span>
            {!isActive(group.status) ? (
              <button
                type="button"
                onClick={() => dismiss([group])}
                className="rounded p-0.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
                aria-label="Ausblenden"
              >
                <X className="size-3.5" />
              </button>
            ) : null}
          </div>
          <div className="mt-1">
            {group.tasks.map((task) =>
              task.kind === "upload" ? (
                <UploadStep key={task.id} task={task} />
              ) : (
                <ServerStep key={task.id} task={task} orgId={orgId} />
              ),
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function summary(groups: ActivityGroup[]): string {
  const active = groups.filter((group) => isActive(group.status)).length;
  if (active > 0) return active === 1 ? "1 Vorgang läuft" : `${active} Vorgänge laufen`;
  if (groups.some((group) => group.status === "failed")) return "Mit Fehlern beendet";
  if (groups.length === 0) return "Hintergrund-Vorgänge";
  return "Alles erledigt";
}

/** Short state for the collapsed tray, e.g. "Upload 45 %". */
function headline(groups: ActivityGroup[]): { text: string; tone: "active" | "failed" | "done" } {
  const tasks = groups.flatMap((group) => group.tasks);
  const upload = tasks.find((task) => task.kind === "upload" && task.status === "running");
  if (upload?.kind === "upload") {
    const running = tasks.filter((task) => task.kind === "upload" && task.status === "running");
    const total = running.reduce(
      (sum, task) => sum + (task.kind === "upload" ? task.upload.size : 0),
      0,
    );
    const done = running.reduce(
      (sum, task) => sum + (task.kind === "upload" ? task.upload.uploaded : 0),
      0,
    );
    return { text: `Upload ${Math.floor((done / Math.max(total, 1)) * 100)} %`, tone: "active" };
  }
  const exporting = tasks.find((task) => task.kind === "export" && task.status === "running");
  if (exporting && exporting.kind === "export" && exporting.activity.progress) {
    return { text: `Export ${Math.floor(exporting.activity.progress)} %`, tone: "active" };
  }
  const transcript = tasks.find((task) => task.kind === "transcript" && task.status === "running");
  if (transcript) return { text: "Untertitel werden erkannt", tone: "active" };
  if (tasks.some((task) => task.kind === "cutout" && task.status === "running")) {
    return { text: "Hintergrund wird entfernt", tone: "active" };
  }
  const matte = tasks.find((task) => task.kind === "matte" && task.status === "running");
  if (matte && matte.kind === "matte") {
    return {
      text: matte.activity.progress
        ? `Person freistellen ${Math.floor(matte.activity.progress)} %`
        : "Person wird freigestellt",
      tone: "active",
    };
  }
  if (tasks.some((task) => task.kind === "media" && task.status === "running")) {
    return { text: "Video wird vorbereitet", tone: "active" };
  }
  if (exporting) return { text: "Export läuft", tone: "active" };
  if (tasks.some((task) => task.status === "queued")) return { text: "Wartet …", tone: "active" };
  if (groups.some((group) => group.status === "failed")) return { text: "Fehler", tone: "failed" };
  return { text: "Fertig", tone: "done" };
}

// ---------------------------------------------------------------------------
// Tray state (per browser tab, survives switching between pages and editor)
// ---------------------------------------------------------------------------

const TRAY_KEY = "cva.activity.open";
let trayOpen = readTrayOpen();
/** Running groups the tray has already shown; a new one opens the tray again. */
const announced = new Set<string>();
const trayListeners = new Set<() => void>();

function readTrayOpen(): boolean {
  try {
    return sessionStorage.getItem(TRAY_KEY) === "1";
  } catch {
    return false;
  }
}

function setTrayOpen(open: boolean) {
  if (trayOpen === open) return;
  trayOpen = open;
  try {
    sessionStorage.setItem(TRAY_KEY, open ? "1" : "0");
  } catch {
    // private mode: remembered for this page only
  }
  for (const listener of trayListeners) listener();
}

function useTrayOpen() {
  return useSyncExternalStore(
    (listener) => {
      trayListeners.add(listener);
      return () => trayListeners.delete(listener);
    },
    () => trayOpen,
  );
}

/**
 * The user's background work in the bottom right corner, on every page and
 * in the editor. Collapsed it is a small status pill (or just an icon when
 * nothing happens); it opens by itself when something new starts. In the
 * editor it also shows what happens with the files of the open document.
 */
export function ActivityTray({
  orgId,
  assetIds,
}: {
  orgId: string | null;
  assetIds?: ReadonlySet<string>;
}) {
  const groups = useActivity(orgId).filter(
    (group) => group.mine || (group.assetId !== null && Boolean(assetIds?.has(group.assetId))),
  );
  const open = useTrayOpen();
  const running = groups.filter((group) => isActive(group.status)).map((group) => group.key);
  const runningKey = running.join("|");
  useEffect(() => {
    const fresh = runningKey.split("|").filter((key) => key && !announced.has(key));
    for (const key of fresh) announced.add(key);
    if (fresh.length > 0) setTrayOpen(true);
  }, [runningKey]);
  if (!orgId) return null;
  const active = running.length > 0;
  const finished = groups.filter((group) => !isActive(group.status));

  if (!open) {
    if (groups.length === 0) {
      return (
        <button
          type="button"
          onClick={() => setTrayOpen(true)}
          title="Hintergrund-Vorgänge"
          aria-label="Hintergrund-Vorgänge anzeigen"
          className="fixed right-4 bottom-4 z-40 inline-flex size-9 items-center justify-center rounded-full border border-zinc-200 bg-white text-zinc-500 shadow-md hover:text-zinc-900"
        >
          <ListChecks className="size-4" />
        </button>
      );
    }
    const state = headline(groups);
    return (
      <button
        type="button"
        onClick={() => setTrayOpen(true)}
        className={cn(
          "fixed right-4 bottom-4 z-40 inline-flex h-9 items-center gap-2 rounded-full border bg-white px-3.5 text-xs font-medium shadow-md",
          state.tone === "active" && "border-brand-200 text-brand-700",
          state.tone === "failed" && "border-red-200 text-red-700",
          state.tone === "done" && "border-emerald-200 text-emerald-700",
        )}
      >
        {state.tone === "active" ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : state.tone === "failed" ? (
          <CircleAlert className="size-3.5" />
        ) : (
          <CheckCircle2 className="size-3.5" />
        )}
        {state.text}
        {groups.length > 1 ? (
          <span className="rounded-full bg-zinc-100 px-1.5 text-[11px] text-zinc-600">
            {groups.length}
          </span>
        ) : null}
        <ChevronUp className="size-3.5 text-zinc-400" />
      </button>
    );
  }

  return (
    <div className="fixed right-4 bottom-4 z-40 w-96 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-lg">
      <div className="flex items-center justify-between gap-2 border-b border-zinc-100 px-4 py-2.5">
        <span className="flex items-center gap-2 text-sm font-medium">
          {active ? (
            <Loader2 className="size-4 animate-spin text-brand-600" />
          ) : groups.some((group) => group.status === "failed") ? (
            <CircleAlert className="size-4 text-red-600" />
          ) : groups.length > 0 ? (
            <CheckCircle2 className="size-4 text-emerald-600" />
          ) : (
            <ListChecks className="size-4 text-zinc-400" />
          )}
          {summary(groups)}
        </span>
        <span className="flex items-center gap-1">
          {finished.length > 0 ? (
            <button
              type="button"
              onClick={() => {
                dismiss(finished);
                // Nothing left to show: back to the small button.
                if (!active) setTrayOpen(false);
              }}
              className="rounded px-1.5 py-0.5 text-xs text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
            >
              Erledigte ausblenden
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setTrayOpen(false)}
            className="rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
            aria-label="Einklappen"
            title="Einklappen"
          >
            <ChevronDown className="size-4" />
          </button>
        </span>
      </div>
      <div className="max-h-96 overflow-y-auto">
        <ActivityList groups={groups} orgId={orgId} />
      </div>
    </div>
  );
}
